import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Image, Tag, ArrowLeft, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import UseAxiosSecure from '../hooks/UseAxiosSecure';

const AddBlogAdmin = () => {
    const navigate = useNavigate();
    const axiosSecure = UseAxiosSecure();

    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        author: 'Getsoko Team',
        metaTitle: '',
        metaDescription: '',
        keywords: '',
        content: ''
    });

    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // Handle standard input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            
            // Auto-generate slug from title if slug hasn't been manually altered
            if (name === 'title' && !prev.slug) {
                updated.slug = value
                    .toLowerCase()
                    .replace(/[^a-z0-9 -]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-');
            }
            return updated;
        });
    };

    // Handle File Input Selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setThumbnailFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    // Form Submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');
        setSuccessMsg('');

        if (!thumbnailFile) {
            setErrorMsg('Please select a thumbnail image.');
            return;
        }

        setIsSubmitting(true);

        try {
            // Build Multipart FormData for Multer/Cloudinary
            const submissionData = new FormData();
            submissionData.append('thumbnail', thumbnailFile);
            submissionData.append('title', formData.title);
            submissionData.append('slug', formData.slug);
            submissionData.append('author', formData.author);
            submissionData.append('content', formData.content);
            submissionData.append('metaTitle', formData.metaTitle || formData.title);
            submissionData.append('metaDescription', formData.metaDescription);
            submissionData.append('keywords', formData.keywords);

            const response = await axiosSecure.post('/blogs', submissionData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                setSuccessMsg('Blog post created and published successfully!');
                setTimeout(() => {
                    navigate('/admin/blog-list');
                }, 1500);
            }
        } catch (err) {
            console.error('Error publishing blog:', err);
            setErrorMsg(err?.response?.data?.message || 'Failed to publish blog post.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto font-sans text-white">
            
            {/* Header Bar */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => navigate('/admin/blog-list')} 
                        className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-slate-300 hover:text-white transition-all"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-black tracking-wide">Create SEO Blog Post</h1>
                        <p className="text-xs text-slate-400">Publish articles to improve Getsoko search engine indexing</p>
                    </div>
                </div>
            </div>

            {/* Notification Alerts */}
            {errorMsg && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl flex items-center gap-3 text-red-400 text-sm">
                    <AlertCircle size={20} className="shrink-0" />
                    <span>{errorMsg}</span>
                </div>
            )}

            {successMsg && (
                <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center gap-3 text-emerald-400 text-sm">
                    <CheckCircle2 size={20} className="shrink-0" />
                    <span>{successMsg}</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* LEFT COLUMN: Main Post Content (2 Cols) */}
                    <div className="lg:col-span-2 space-y-5">
                        
                        {/* Title Input */}
                        <div className="bg-[#1a103c]/60 border border-white/10 rounded-2xl p-5 space-y-2 backdrop-blur-xl">
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                                Article Title <span className="text-pink-500">*</span>
                            </label>
                            <input 
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                placeholder="e.g. 5 Strategies to Optimize Multi-Branch Restaurant Operations"
                                className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:outline-none focus:border-pink-500/50 text-sm"
                            />
                        </div>

                        {/* URL Slug Input */}
                        <div className="bg-[#1a103c]/60 border border-white/10 rounded-2xl p-5 space-y-2 backdrop-blur-xl">
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                                SEO URL Slug (Auto-generated)
                            </label>
                            <div className="flex items-center bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-xs text-slate-400">
                                <span className="text-slate-600 shrink-0">getsoko.app/blog/</span>
                                <input 
                                    type="text"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleChange}
                                    required
                                    placeholder="5-strategies-to-optimize-multi-branch-restaurant-operations"
                                    className="w-full bg-transparent border-none outline-none text-pink-400 ml-1 text-xs"
                                />
                            </div>
                        </div>

                        {/* Content Body Textarea */}
                        <div className="bg-[#1a103c]/60 border border-white/10 rounded-2xl p-5 space-y-2 backdrop-blur-xl">
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                                Article Content (HTML / Plain Text) <span className="text-pink-500">*</span>
                            </label>
                            <textarea 
                                name="content"
                                rows="12"
                                value={formData.content}
                                onChange={handleChange}
                                required
                                placeholder="Write or paste your article HTML content here..."
                                className="w-full bg-slate-900/60 border border-white/10 rounded-xl p-4 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-pink-500/50 text-sm leading-relaxed custom-scrollbar"
                            ></textarea>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: SEO Settings & Thumbnail Upload (1 Col) */}
                    <div className="space-y-5">
                        
                        {/* Thumbnail Upload */}
                        <div className="bg-[#1a103c]/60 border border-white/10 rounded-2xl p-5 space-y-3 backdrop-blur-xl">
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                                <Image size={16} className="text-pink-500" />
                                Featured Thumbnail <span className="text-pink-500">*</span>
                            </label>

                            {imagePreview ? (
                                <div className="relative rounded-xl overflow-hidden border border-white/10 group h-40">
                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                    <button 
                                        type="button" 
                                        onClick={() => { setThumbnailFile(null); setImagePreview(null); }}
                                        className="absolute top-2 right-2 p-1.5 bg-red-600/80 hover:bg-red-600 text-white rounded-lg text-xs"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <label className="flex flex-col items-center justify-center h-40 bg-slate-900/60 border-2 border-dashed border-white/10 hover:border-pink-500/50 rounded-xl cursor-pointer transition-all">
                                    <Image size={28} className="text-slate-500 mb-2" />
                                    <span className="text-xs text-slate-400 font-bold">Upload Thumbnail</span>
                                    <span className="text-[10px] text-slate-600">PNG, JPG, WEBP (Cloudinary)</span>
                                    <input 
                                        type="file" 
                                        accept="image/*" 
                                        onChange={handleFileChange} 
                                        className="hidden" 
                                    />
                                </label>
                            )}
                        </div>

                        {/* SEO Fields Settings */}
                        <div className="bg-[#1a103c]/60 border border-white/10 rounded-2xl p-5 space-y-4 backdrop-blur-xl">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-400 border-b border-white/5 pb-2">
                                Search Engine Metadata
                            </h3>

                            {/* Meta Title */}
                            <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                    <label className="text-[11px] font-bold text-slate-400">Meta Title</label>
                                    <span className={`text-[10px] ${formData.metaTitle.length > 60 ? 'text-red-400' : 'text-slate-500'}`}>
                                        {formData.metaTitle.length}/60
                                    </span>
                                </div>
                                <input 
                                    type="text"
                                    name="metaTitle"
                                    value={formData.metaTitle}
                                    onChange={handleChange}
                                    placeholder="Title for Google search results"
                                    className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-slate-600 text-xs focus:outline-none focus:border-pink-500/50"
                                />
                            </div>

                            {/* Meta Description */}
                            <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                    <label className="text-[11px] font-bold text-slate-400">Meta Description</label>
                                    <span className={`text-[10px] ${formData.metaDescription.length > 160 ? 'text-red-400' : 'text-slate-500'}`}>
                                        {formData.metaDescription.length}/160
                                    </span>
                                </div>
                                <textarea 
                                    name="metaDescription"
                                    rows="3"
                                    value={formData.metaDescription}
                                    onChange={handleChange}
                                    placeholder="Brief summary appearing under search titles..."
                                    className="w-full bg-slate-900/60 border border-white/10 rounded-xl p-3 text-slate-300 placeholder-slate-600 text-xs focus:outline-none focus:border-pink-500/50 resize-none"
                                ></textarea>
                            </div>

                            {/* Keywords */}
                            <div className="space-y-1">
                                <label className="text-[11px] font-bold text-slate-400 flex items-center gap-1">
                                    <Tag size={12} /> Keywords (Comma Separated)
                                </label>
                                <input 
                                    type="text"
                                    name="keywords"
                                    value={formData.keywords}
                                    onChange={handleChange}
                                    placeholder="restaurant pos, branch manager, inventory"
                                    className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-slate-600 text-xs focus:outline-none focus:border-pink-500/50"
                                />
                            </div>

                            {/* Author */}
                            <div className="space-y-1">
                                <label className="text-[11px] font-bold text-slate-400">Author</label>
                                <input 
                                    type="text"
                                    name="author"
                                    value={formData.author}
                                    onChange={handleChange}
                                    className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-3 py-2 text-white text-xs focus:outline-none focus:border-pink-500/50"
                                />
                            </div>
                        </div>

                        {/* Publish Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full py-3.5 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 hover:from-pink-500 hover:to-indigo-500 text-white font-bold rounded-2xl shadow-lg shadow-purple-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    <span>Uploading to Cloudinary...</span>
                                </>
                            ) : (
                                <>
                                    <Send size={16} />
                                    <span>Publish Post</span>
                                </>
                            )}
                        </button>

                    </div>
                </div>
            </form>
        </div>
    );
};

export default AddBlogAdmin;